import { useRef } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { BellIcon } from '@xantory/icons';
import { useT, useRelTime } from '@xantory/i18n';
import { useChromePopover } from '../../hooks/use-chrome-popover';
import { styles } from './notification-bell.styles';

/** One bell item — presentational; the app layer supplies data + actions. */
export interface BellNotification {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  /** Epoch milliseconds. */
  createdAt: number;
  read: boolean;
  acked: boolean;
  /** In-app destination for the thing this notification is about. Absent ⇒ the
   *  row has nowhere to go and stays a mark-read control. */
  href?: string | null;
}
interface NotificationBellProps {
  unread: number;
  items: BellNotification[];
  onRead: (id: string) => void;
  onAck: (id: string) => void;
  onReadAll: () => void;
  /** An actionable row was opened: the app marks it read and puts the app into
   *  the right context (site) — navigation itself is the anchor's own href. */
  onOpen?: (id: string) => void;
  /** Link to the full Notifications page (R-112) — the dropdown's "See all" footer. */
  seeAllHref?: string;
}

/** The top-bar bell (R-060): unread badge + dropdown in the shared menu idiom. */
export function NotificationBell({
  unread,
  items,
  onRead,
  onAck,
  onReadAll,
  onOpen,
  seeAllHref
}: NotificationBellProps) {
  const t = useT('ui');
  const rel = useRelTime();
  // §24 — the scrollbar is hidden everywhere we own the scroller.
  const listProps = stylex.props(styles.list);
  const btnRef = useRef<HTMLButtonElement>(null);
  // Shared with every other top-bar dropdown, so opening one closes the rest.
  const {
    open,
    close,
    onTriggerClick
  } = useChromePopover(btnRef);
  return <div {...stylex.props(styles.wrap)}>
      <button ref={btnRef} type="button" aria-label={unread > 0 ? `${t('notifications')} (${unread})` : t('notifications')} aria-haspopup="dialog" aria-expanded={open} onClick={onTriggerClick} {...stylex.props(styles.iconBtn)}>
        {unread > 0 && <span aria-hidden="true" {...stylex.props(styles.badge)}>
            {unread > 99 ? '99+' : unread}
          </span>}
        <BellIcon size={18} />
      </button>

      {open && <div onClick={e => e.stopPropagation()} {...stylex.props(styles.menu)}>
          <div {...stylex.props(styles.head)}>
            <span {...stylex.props(styles.headTitle)}>{t('notifications')}</span>
            {unread > 0 && <button type="button" onClick={onReadAll} {...stylex.props(styles.readAll)}>
                {t('markAllRead')}
              </button>}
          </div>

          {items.length === 0 ? <div {...stylex.props(styles.empty)}>{t('noNotifications')}</div> :
      // Each row is inert; its BODY is the real control, so Acknowledge
      // isn't nested inside another interactive (axe: nested-interactive).
      // The body is an ANCHOR when the notification has a destination —
      // that is the whole point of a notification, and it must stay
      // clickable after it has been read (a disabled button would not).
      <div {...listProps} className={[listProps.className, 'scroll-hidden'].filter(Boolean).join(' ')}>
              {items.map(n => <div key={n.id} {...stylex.props(styles.item, !n.read && styles.itemUnread)}>
                  <span {...stylex.props(styles.dot, styles[n.severity])} />
                  {n.href ? <a href={n.href} onClick={() => {
            onOpen?.(n.id);
            close();
          }} {...stylex.props(styles.itemBody, styles.itemLink)}>
                      <div {...stylex.props(styles.itemTitle, n.read ? styles.itemTitleRead : styles.itemTitleUnread)}>
                        {n.title}
                      </div>
                      <div {...stylex.props(styles.itemMeta, n.read && styles.itemMetaRead)}>
                        {rel(n.createdAt)}
                      </div>
                    </a> : <button type="button" disabled={n.read} onClick={() => onRead(n.id)} {...stylex.props(styles.itemBody)}>
                      <div {...stylex.props(styles.itemTitle, n.read ? styles.itemTitleRead : styles.itemTitleUnread)}>
                        {n.title}
                      </div>
                      <div {...stylex.props(styles.itemMeta, n.read && styles.itemMetaRead)}>
                        {rel(n.createdAt)}
                      </div>
                    </button>}
                  {/* No "Open →" affordance: the whole row body IS the link, so a
                      second control saying so was noise — and dropping it also
                      removes the duplicate-destination anchor the row had to hide
                      from assistive tech. */}
                  {n.severity === 'critical' && !n.acked && <button type="button" onClick={e => {
            e.stopPropagation();
            onAck(n.id);
          }} {...stylex.props(styles.ackBtn)}>
                      {t('acknowledge')}
                    </button>}
                </div>)}
            </div>}
          {seeAllHref && <a href={seeAllHref} onClick={() => close()} {...stylex.props(styles.seeAll)}>
              {t('seeAll')}
            </a>}
        </div>}
    </div>;
}